import {
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { getCurrentUser } from "../shared/getCurrentUser";
import {
  AdminUserDetails,
  ApiError,
  queryKeys,
  RestApi,
} from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";

function dateOrDash(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function textOrDash(value?: string | number | null): string {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  return String(value);
}

function YesNo({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge color={value ? "green" : "gray"} variant="light" size="sm">
      {value ? t("admin.yes") : t("admin.no")}
    </Badge>
  );
}

function Stored({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return (
    <Text size="sm">{value ? t("admin.stored") : t("admin.notStored")}</Text>
  );
}

function Fields({
  rows,
}: {
  rows: Array<{ label: string; value: React.ReactNode }>;
}) {
  return (
    <Stack gap={0}>
      {rows.map((row, index) => (
        <Box key={`${row.label}-${index}`}>
          {index > 0 && <Divider color="gray.1" />}
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="sm"
            py="xs"
          >
            <Text size="sm" c="dimmed" fw={500} miw={160}>
              {row.label}
            </Text>
            <Box style={{ flex: "1 1 220px", textAlign: "right", minWidth: 0 }}>
              {row.value}
            </Box>
          </Group>
        </Box>
      ))}
    </Stack>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" style={{ wordBreak: "break-word" }}>
      {children}
    </Text>
  );
}

function UserIdLink({ userId }: { userId?: string | null }) {
  if (!userId) {
    return <Cell>—</Cell>;
  }
  return (
    <Anchor
      component={Link}
      to={`/admin/user/${encodeURIComponent(userId)}`}
      size="sm"
      style={{ wordBreak: "break-all" }}
    >
      {userId}
    </Anchor>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      id={id}
      p="lg"
      style={{
        background: "white",
        border: "1px solid var(--mantine-color-gray-2)",
        borderRadius: "var(--mantine-radius-md)",
        scrollMarginTop: 80,
      }}
    >
      <Title order={4} c="charcoal.8" mb="sm">
        {title}
      </Title>
      {children}
    </Box>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <Text c="dimmed" size="sm">
      {children}
    </Text>
  );
}

function LicenseTable({
  rows,
  includeDistributor,
}: {
  rows: Array<{
    distributorUserId?: string;
    company?: string;
    email?: string;
    licenseId?: string;
    rowKey?: string;
    sent?: string;
    acceptedBy?: string;
    accepted?: string;
  }>;
  includeDistributor: boolean;
}) {
  return (
    <Table.ScrollContainer minWidth={includeDistributor ? 860 : 640}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            {includeDistributor && <Table.Th>Distributor</Table.Th>}
            {includeDistributor && <Table.Th>Company</Table.Th>}
            <Table.Th>Email</Table.Th>
            <Table.Th>License</Table.Th>
            <Table.Th>Sent</Table.Th>
            <Table.Th>Accepted by</Table.Th>
            <Table.Th>Accepted</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={`${row.licenseId}-${row.rowKey}-${row.email}`}>
              {includeDistributor && (
                <Table.Td>
                  <UserIdLink userId={row.distributorUserId} />
                </Table.Td>
              )}
              {includeDistributor && (
                <Table.Td>
                  <Cell>{textOrDash(row.company)}</Cell>
                </Table.Td>
              )}
              <Table.Td>
                <Cell>{textOrDash(row.email)}</Cell>
              </Table.Td>
              <Table.Td>
                <Cell>{textOrDash(row.licenseId || row.rowKey)}</Cell>
              </Table.Td>
              <Table.Td>
                <Cell>{dateOrDash(row.sent)}</Cell>
              </Table.Td>
              <Table.Td>
                <UserIdLink userId={row.acceptedBy} />
              </Table.Td>
              <Table.Td>
                <Cell>{dateOrDash(row.accepted)}</Cell>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function AccountSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  const user = details.User;
  return (
    <Section id="account" title={t("admin.sectionAccount")}>
      <Text size="xs" c="dimmed" mb="sm">
        {t("admin.secretsHidden")}
      </Text>
      <Fields
        rows={[
          { label: "User ID", value: <Cell>{user.UserId}</Cell> },
          { label: "Name", value: <Cell>{textOrDash(user.Name)}</Cell> },
          { label: "Email", value: <Cell>{textOrDash(user.Email)}</Cell> },
          {
            label: "Account email",
            value: <Cell>{textOrDash(user.AccountEmail)}</Cell>,
          },
          {
            label: "Account type",
            value: <Cell>{textOrDash(user.AccountType)}</Cell>,
          },
          {
            label: "Email confirmed",
            value: <YesNo value={user.EmailConfirmed} />,
          },
          {
            label: "Email confirmed date",
            value: <Cell>{dateOrDash(user.EmailConfirmedDate)}</Cell>,
          },
          {
            label: "Confirmation id",
            value: <Cell>{textOrDash(user.ConfirmEmailGuid)}</Cell>,
          },
          {
            label: "Refresh token",
            value: <Stored value={user.HasEncryptedRefreshToken} />,
          },
          { label: "Admin", value: <YesNo value={user.IsAdmin} /> },
          {
            label: "Partition key",
            value: <Cell>{textOrDash(user.PartitionKey)}</Cell>,
          },
          { label: "Row key", value: <Cell>{textOrDash(user.RowKey)}</Cell> },
          {
            label: "Registered",
            value: <Cell>{dateOrDash(user.Timestamp)}</Cell>,
          },
          { label: "ETag", value: <Cell>{textOrDash(user.ETag)}</Cell> },
        ]}
      />
    </Section>
  );
}

function BillingSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  const user = details.User;
  const amount =
    user.PaymentId || user.PaymentCurrency || user.PaymentAmount
      ? [user.PaymentAmount, user.PaymentCurrency]
          .filter((part) => part !== undefined && part !== null && part !== "")
          .join(" ")
      : "";
  return (
    <Section id="billing" title={t("admin.sectionBilling")}>
      <Fields
        rows={[
          { label: "Bought", value: <YesNo value={user.Bought} /> },
          { label: "Prepaid", value: <YesNo value={details.IsPrepaid} /> },
          {
            label: "Payment date",
            value: <Cell>{dateOrDash(user.PaymentDate)}</Cell>,
          },
          {
            label: "Payment id",
            value: <Cell>{textOrDash(user.PaymentId)}</Cell>,
          },
          {
            label: "Payment method",
            value: <Cell>{textOrDash(user.PaymentMethod)}</Cell>,
          },
          { label: "Amount", value: <Cell>{textOrDash(amount)}</Cell> },
          {
            label: "Stripe customer",
            value: <Cell>{textOrDash(user.StripeCustomerId)}</Cell>,
          },
          {
            label: "Stripe subscription",
            value: <Cell>{textOrDash(user.StripeSubscriptionId)}</Cell>,
          },
        ]}
      />
    </Section>
  );
}

function LocalSignInSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  const signIn = details.LocalSignIn;
  return (
    <Section id="local-sign-in" title={t("admin.sectionLocalSignIn")}>
      {signIn ? (
        <Fields
          rows={[
            { label: "Email", value: <Cell>{textOrDash(signIn.Email)}</Cell> },
            { label: "Password", value: <Stored value={signIn.HasPassword} /> },
            {
              label: "Failed attempts",
              value: <Cell>{signIn.FailedAttemptCount}</Cell>,
            },
            {
              label: "Lockout ends",
              value: <Cell>{dateOrDash(signIn.LockoutEndUtc)}</Cell>,
            },
            {
              label: "Password reset token",
              value: <Stored value={signIn.HasPasswordResetToken} />,
            },
            {
              label: "Reset expiry",
              value: <Cell>{dateOrDash(signIn.PasswordResetExpiryUtc)}</Cell>,
            },
          ]}
        />
      ) : (
        <EmptyNote>{t("admin.noLocalSignIn")}</EmptyNote>
      )}
    </Section>
  );
}

function DistributorSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  const distributor = details.Distributor;
  return (
    <Section id="distributor" title={t("admin.sectionDistributor")}>
      {distributor ? (
        <Stack gap="md">
          <Fields
            rows={[
              {
                label: "Company",
                value: <Cell>{textOrDash(distributor.CompanyName)}</Cell>,
              },
              {
                label: "Phone",
                value: <Cell>{textOrDash(distributor.PhoneNumber)}</Cell>,
              },
              {
                label: "Business id",
                value: <Cell>{textOrDash(distributor.BusinessId)}</Cell>,
              },
              {
                label: "Street",
                value: <Cell>{textOrDash(distributor.Address)}</Cell>,
              },
              {
                label: "Street 2",
                value: <Cell>{textOrDash(distributor.Street2)}</Cell>,
              },
              {
                label: "Number",
                value: <Cell>{textOrDash(distributor.Number)}</Cell>,
              },
              {
                label: "City",
                value: <Cell>{textOrDash(distributor.City)}</Cell>,
              },
              {
                label: "State",
                value: <Cell>{textOrDash(distributor.State)}</Cell>,
              },
              {
                label: "Country",
                value: <Cell>{textOrDash(distributor.Country)}</Cell>,
              },
              { label: "ZIP", value: <Cell>{textOrDash(distributor.Zip)}</Cell> },
              {
                label: "Authorized",
                value: <YesNo value={distributor.DistribuidorAutorizado} />,
              },
              {
                label: "Authorized by",
                value: <UserIdLink userId={distributor.AutorizadoPorUsuario} />,
              },
              {
                label: "Licenses bought",
                value: <Cell>{distributor.LicensesBought}</Cell>,
              },
              {
                label: "Licenses available",
                value: <Cell>{distributor.AvailableLicenses}</Cell>,
              },
              {
                label: "Partition key",
                value: <Cell>{textOrDash(distributor.PartitionKey)}</Cell>,
              },
              {
                label: "Row key",
                value: <Cell>{textOrDash(distributor.RowKey)}</Cell>,
              },
              {
                label: "Updated",
                value: <Cell>{dateOrDash(distributor.Timestamp)}</Cell>,
              },
            ]}
          />
          <Title order={5}>{t("admin.sectionPayments")}</Title>
          {distributor.Payments.length === 0 ? (
            <EmptyNote>{t("admin.emptySection")}</EmptyNote>
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Payment id</Table.Th>
                    <Table.Th>Method</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Licenses</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {distributor.Payments.map((payment) => (
                    <Table.Tr key={payment.PaymentId || payment.RowKey}>
                      <Table.Td>
                        <Cell>{dateOrDash(payment.PaymentDate)}</Cell>
                      </Table.Td>
                      <Table.Td>
                        <Cell>{textOrDash(payment.PaymentId)}</Cell>
                      </Table.Td>
                      <Table.Td>
                        <Cell>{textOrDash(payment.PaymentMethod)}</Cell>
                      </Table.Td>
                      <Table.Td>
                        <Cell>
                          {textOrDash(
                            [payment.PaymentAmount, payment.PaymentCurrency]
                              .filter((part) => part !== undefined && part !== "")
                              .join(" "),
                          )}
                        </Cell>
                      </Table.Td>
                      <Table.Td>
                        <Cell>{payment.LicenseCount}</Cell>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          <Title order={5}>{t("admin.sectionLicensesIssued")}</Title>
          {distributor.LicensesIssued.length === 0 ? (
            <EmptyNote>{t("admin.emptySection")}</EmptyNote>
          ) : (
            <LicenseTable
              includeDistributor={false}
              rows={distributor.LicensesIssued.map((license) => ({
                email: license.LicenseInvitationEmail,
                licenseId: license.LicenseId,
                rowKey: license.RowKey,
                sent: license.InsertionTime,
                acceptedBy: license.AcceptedByUserId,
                accepted: license.AcceptedDate,
              }))}
            />
          )}
        </Stack>
      ) : (
        <EmptyNote>{t("admin.notDistributor")}</EmptyNote>
      )}
    </Section>
  );
}

function LabelsSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  return (
    <Section
      id="labels"
      title={`${t("admin.sectionLabels")} (${details.Labels.length})`}
    >
      {details.Labels.length === 0 ? (
        <EmptyNote>{t("admin.emptySection")}</EmptyNote>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Modified</Table.Th>
                <Table.Th>Public</Table.Th>
                <Table.Th>Version</Table.Th>
                <Table.Th>Blob</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {details.Labels.map((label) => (
                <Table.Tr key={label.RowKey || label.PointerToBlob}>
                  <Table.Td>
                    <Cell>{textOrDash(label.FileName)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{dateOrDash(label.CreatedDate)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{dateOrDash(label.LastModifiedDate)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <YesNo value={label.IsPublic} />
                  </Table.Td>
                  <Table.Td>
                    <Cell>{label.Version}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(label.PointerToBlob)}</Cell>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Section>
  );
}

function SessionsSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  return (
    <Section
      id="sessions"
      title={`${t("admin.sectionSessions")} (${details.AppSessions.length})`}
    >
      {details.AppSessions.length === 0 ? (
        <EmptyNote>{t("admin.emptySection")}</EmptyNote>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Created</Table.Th>
              <Table.Th>Expires</Table.Th>
              <Table.Th>Revoked</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {details.AppSessions.map((session, index) => (
              <Table.Tr key={`${session.CreatedUtc}-${index}`}>
                <Table.Td>
                  <Cell>{dateOrDash(session.CreatedUtc)}</Cell>
                </Table.Td>
                <Table.Td>
                  <Cell>{dateOrDash(session.ExpiryUtc)}</Cell>
                </Table.Td>
                <Table.Td>
                  <YesNo value={session.Revoked} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Section>
  );
}

function LoginCodesSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  return (
    <Section
      id="login-codes"
      title={`${t("admin.sectionLoginCodes")} (${details.AppLoginCodes.length})`}
    >
      {details.AppLoginCodes.length === 0 ? (
        <EmptyNote>{t("admin.emptySection")}</EmptyNote>
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Redirect</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Consumed</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>State</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {details.AppLoginCodes.map((code, index) => (
                <Table.Tr key={`${code.ExpiryUtc}-${index}`}>
                  <Table.Td>
                    <Cell>{textOrDash(code.RedirectUri)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{dateOrDash(code.ExpiryUtc)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <YesNo value={code.Consumed} />
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(code.MintSource)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(code.ClientState)}</Cell>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Section>
  );
}

function DemoSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  return (
    <Section
      id="demo-downloads"
      title={`${t("admin.sectionDemoDownloads")} (${details.DemoDownloads.length})`}
    >
      {details.DemoDownloads.length === 0 ? (
        <EmptyNote>{t("admin.emptySection")}</EmptyNote>
      ) : (
        <Table.ScrollContainer minWidth={860}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>IP</Table.Th>
                <Table.Th>Language</Table.Th>
                <Table.Th>Referrer</Table.Th>
                <Table.Th>User agent</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {details.DemoDownloads.map((download) => (
                <Table.Tr key={download.RowKey || download.DownloadedAtUtc}>
                  <Table.Td>
                    <Cell>{dateOrDash(download.DownloadedAtUtc)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(download.Email)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(download.IpAddress)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(download.AcceptLanguage)}</Cell>
                  </Table.Td>
                  <Table.Td>
                    <Cell>{textOrDash(download.Referrer)}</Cell>
                  </Table.Td>
                  <Table.Td maw={240}>
                    <Text size="sm" truncate="end" title={download.UserAgent}>
                      {textOrDash(download.UserAgent)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Section>
  );
}

function ReceivedSection({ details }: { details: AdminUserDetails }) {
  const { t } = useTranslation();
  return (
    <Section
      id="licenses-received"
      title={`${t("admin.sectionLicensesReceived")} (${details.LicensesReceived.length})`}
    >
      {details.LicensesReceived.length === 0 ? (
        <EmptyNote>{t("admin.emptySection")}</EmptyNote>
      ) : (
        <LicenseTable
          includeDistributor
          rows={details.LicensesReceived.map((item) => ({
            distributorUserId: item.DistributorUserId,
            company: item.DistributorCompanyName,
            email: item.License?.LicenseInvitationEmail,
            licenseId: item.License?.LicenseId,
            rowKey: item.License?.RowKey,
            sent: item.License?.InsertionTime,
            acceptedBy: item.License?.AcceptedByUserId,
            accepted: item.License?.AcceptedDate,
          }))}
        />
      )}
    </Section>
  );
}

export function AdminUserDetailsPage() {
  const { t } = useTranslation();
  const { userId = "" } = useParams();
  const queryClient = useQueryClient();
  const currentUser = getCurrentUser();
  const detailsQuery = useQuery({
    queryKey: queryKeys.adminUser(userId),
    queryFn: () => RestApi.getAdminUserDetails(userId),
    enabled: userId.length > 0,
    retry: (count, error) =>
      !(error instanceof ApiError && error.statusCode === 404) && count < 2,
  });

  const toggleAdminMutation = useMutation({
    mutationFn: () => RestApi.toggleAdmin(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminUser(userId),
      });
    },
  });

  const toggleBoughtMutation = useMutation({
    mutationFn: () => RestApi.toggleBought(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminUser(userId),
      });
    },
  });

  const notFound =
    !userId ||
    (detailsQuery.isError &&
      detailsQuery.error instanceof ApiError &&
      detailsQuery.error.statusCode === 404);
  const data = detailsQuery.data;
  const heading = data?.User.Email || data?.User.Name || data?.User.UserId;

  return (
    <NavBarContainer title={heading || t("admin.userDetailsTitle")}>
      <Stack gap="lg">
        <Anchor component={Link} to="/admin" fw={600}>
          {t("admin.backToAdmin")}
        </Anchor>
        {detailsQuery.isPending && !notFound && <Text>{t("admin.loading")}</Text>}
        {detailsQuery.isError && !notFound && (
          <Text c="red">{t("admin.loadError")}</Text>
        )}
        {notFound && <Text c="red">{t("admin.userNotFound")}</Text>}
        {data && (
          <>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Group gap="xs">
                {data.User.EmailConfirmed && (
                  <Badge color="green" variant="light">
                    Confirmed
                  </Badge>
                )}
                {data.User.IsAdmin && (
                  <Badge color="blue" variant="light">
                    Admin
                  </Badge>
                )}
                <Badge color={data.User.Bought ? "brand" : "gray"} variant="light">
                  {data.User.Bought ? "Bought" : "Unlicensed"}
                </Badge>
                {data.IsPrepaid && (
                  <Badge color="teal" variant="light">
                    Prepaid
                  </Badge>
                )}
              </Group>
              <Group gap="xs">
                {currentUser?.UserId === data.User.UserId && (
                  <Button
                    variant="light"
                    size="xs"
                    loading={toggleBoughtMutation.isPending}
                    onClick={() => toggleBoughtMutation.mutate()}
                  >
                    {t("admin.toggleBought")}
                  </Button>
                )}
                <Button
                  variant="light"
                  size="xs"
                  loading={toggleAdminMutation.isPending}
                  onClick={() => toggleAdminMutation.mutate()}
                >
                  {t("admin.toggleAdmin")}
                </Button>
              </Group>
            </Group>
            <Group gap="md" wrap="wrap">
              <Anchor href="#account" size="sm">
                {t("admin.sectionAccount")}
              </Anchor>
              <Anchor href="#billing" size="sm">
                {t("admin.sectionBilling")}
              </Anchor>
              <Anchor href="#local-sign-in" size="sm">
                {t("admin.sectionLocalSignIn")}
              </Anchor>
              <Anchor href="#distributor" size="sm">
                {t("admin.sectionDistributor")}
              </Anchor>
              <Anchor href="#labels" size="sm">
                {t("admin.sectionLabels")}
              </Anchor>
              <Anchor href="#sessions" size="sm">
                {t("admin.sectionSessions")}
              </Anchor>
              <Anchor href="#login-codes" size="sm">
                {t("admin.sectionLoginCodes")}
              </Anchor>
              <Anchor href="#demo-downloads" size="sm">
                {t("admin.sectionDemoDownloads")}
              </Anchor>
              <Anchor href="#licenses-received" size="sm">
                {t("admin.sectionLicensesReceived")}
              </Anchor>
            </Group>
            <AccountSection details={data} />
            <BillingSection details={data} />
            <LocalSignInSection details={data} />
            <DistributorSection details={data} />
            <LabelsSection details={data} />
            <SessionsSection details={data} />
            <LoginCodesSection details={data} />
            <DemoSection details={data} />
            <ReceivedSection details={data} />
          </>
        )}
      </Stack>
    </NavBarContainer>
  );
}
